import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertCircle } from "lucide-react";
import { DatePicker, TimePicker } from "./DateTimePicker";

const inputCls =
  "w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-sm text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-gold focus:bg-white/15 transition-colors";

const HeroQuote = () => {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [isReturn, setIsReturn] = useState(false);

  // Soft validation — Return must be strictly later than pickup.
  const returnBeforePickup = (() => {
    if (!isReturn || !date || !time || !returnDate || !returnTime) return false;
    const pickupAt = new Date(`${date}T${time}:00`);
    const returnAt = new Date(`${returnDate}T${returnTime}:00`);
    if (Number.isNaN(pickupAt.getTime()) || Number.isNaN(returnAt.getTime())) return false;
    return returnAt <= pickupAt;
  })();

  // 7-hour minimum notice policy — mirror of the backend check.
  const tooSoon = (() => {
    if (!date || !time) return false;
    const pickupAt = new Date(`${date}T${time}:00`);
    if (Number.isNaN(pickupAt.getTime())) return false;
    const minPickup = new Date(Date.now() + 7 * 60 * 60 * 1000);
    return pickupAt < minPickup;
  })();

  const earliestLabel = (() => {
    const minPickup = new Date(Date.now() + 7 * 60 * 60 * 1000);
    return minPickup.toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  })();

  const handleContinue = (e) => {
    e.preventDefault();
    if (!pickup.trim() || !dropoff.trim() || !date || !time) return;
    if (isReturn && (!returnDate || !returnTime)) return;
    if (returnBeforePickup) return;
    if (tooSoon) return;
    sessionStorage.setItem(
      "heroQuote",
      JSON.stringify({
        pickup: pickup.trim(),
        dropoff: dropoff.trim(),
        is_return: isReturn,
        date,
        time,
        return_date: isReturn ? returnDate : "",
        return_time: isReturn ? returnTime : "",
      })
    );
    navigate("/checkout");
  };

  return (
    <form
      onSubmit={handleContinue}
      data-testid="hero-quote-widget"
      className="rounded-3xl glass-strong border border-white/15 p-5 lg:p-6 shadow-2xl shadow-black/30 backdrop-blur-md"
    >
      <div className="text-center font-display text-xl lg:text-2xl text-gold mb-4 tracking-tight">
        Request your transfer
      </div>

      {/* One-way / Return tabs */}
      <div className="grid grid-cols-2 gap-2 rounded-full bg-white/5 border border-white/10 p-1 mb-4">
        {[
          { id: "oneway", label: "One way", active: !isReturn },
          { id: "return", label: "Return", active: isReturn },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            data-testid={`hero-trip-${opt.id}`}
            onClick={() => setIsReturn(opt.id === "return")}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              opt.active ? "bg-gold text-[#0a1830]" : "text-foreground/70 hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* From + To (free-text, manual) */}
      <div className="space-y-3">
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.18em] text-foreground/65 mb-1.5">
            From
          </label>
          <input
            type="text"
            data-testid="hero-quote-pickup"
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
            placeholder="e.g. Veikou 50, 11741 Athens"
            className={inputCls}
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.18em] text-foreground/65 mb-1.5">
            To
          </label>
          <input
            type="text"
            data-testid="hero-quote-dropoff"
            value={dropoff}
            onChange={(e) => setDropoff(e.target.value)}
            placeholder="e.g. Athens International Airport"
            className={inputCls}
            autoComplete="off"
          />
        </div>
        <div className="text-[0.7rem] text-foreground/60 leading-relaxed px-1">
          Please write the full address — street, number and postal code (e.g. <em>Veikou 50, 11741</em>). We'll review your request and email you a price.
        </div>
      </div>

      {/* Date + Time */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.18em] text-foreground/65 mb-1.5">
            Pickup date
          </label>
          <DatePicker testId="hero-quote-date" value={date} onChange={setDate} />
        </div>
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.18em] text-foreground/65 mb-1.5">
            Pickup time
          </label>
          <TimePicker testId="hero-quote-time" value={time} onChange={setTime} />
        </div>
      </div>

      {tooSoon && (
        <div
          data-testid="hero-too-soon"
          className="mt-3 flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-400/30 px-3 py-2 text-xs text-red-200"
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Bookings need at least <strong>7 hours</strong>' notice. Earliest pickup right now:{" "}
            <strong>{earliestLabel}</strong>. For urgent rides, please WhatsApp us.
          </span>
        </div>
      )}

      {/* Return date + time */}
      {isReturn && (
        <div
          data-testid="hero-quote-return-fields"
          className="mt-3 rounded-2xl border border-gold/25 bg-gold/[0.04] px-3 py-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[0.7rem] uppercase tracking-[0.18em] text-foreground/65 mb-1.5">
                Return date
              </label>
              <DatePicker
                testId="hero-quote-return-date"
                value={returnDate}
                onChange={setReturnDate}
                minDate={date || undefined}
              />
            </div>
            <div>
              <label className="block text-[0.7rem] uppercase tracking-[0.18em] text-foreground/65 mb-1.5">
                Return time
              </label>
              <TimePicker
                testId="hero-quote-return-time"
                value={returnTime}
                onChange={setReturnTime}
              />
            </div>
          </div>
          {returnBeforePickup && (
            <div
              data-testid="hero-return-before-pickup"
              className="mt-3 flex items-start gap-2 text-xs text-red-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              Return must be later than the outbound pickup.
            </div>
          )}
        </div>
      )}

      {/* Continue button */}
      <button
        type="submit"
        data-testid="hero-quote-continue"
        disabled={
          !pickup.trim() ||
          !dropoff.trim() ||
          !date ||
          !time ||
          (isReturn && (!returnDate || !returnTime)) ||
          returnBeforePickup ||
          tooSoon
        }
        className="mt-5 group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold text-[#0a1830] px-7 py-4 font-bold text-sm hover:bg-[#f0c878] transition-all shadow-xl shadow-gold/40 ring-1 ring-gold/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gold"
      >
        Continue to Booking
        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </button>

      <div className="mt-3 text-center text-[0.7rem] uppercase tracking-[0.18em] text-foreground/50">
        We'll email you a price · Cancel free up to 12 h before pickup
      </div>
    </form>
  );
};

export default HeroQuote;
